import React, { useState } from 'react';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../../components/ui/table';
import { FileText, Edit, Trash2, Upload, Package } from 'lucide-react';
import { MaterialData } from '../../../../types/materials';
import { Button } from '../../../../../../components/ui/button';
import { DeleteConfirmationDialog, DeleteItem } from '../../../../../../components/ui/DeleteConfirmationDialog';
import { Pagination } from '../../../../../../components/ui/Pagination';
import { deleteMaterials, DeleteMaterialDocument } from '../../../../../../redux/Project/materialSaga';
import { UpdateMaterialDialog } from '../UpdateMaterialDialog';
import { DocumentDownloadModal } from '../../specifications/DocumentDownloadModal';
import { MaterialDocumentUploadModal } from '../MaterialDocumentUploadModal';
import { DocumentTag } from '../../../../../../features/project/types';
import { MiscFilters } from './Filters/MiscFilters';
import { useMiscFilters } from './Filters/hooks/useMiscFilters';
import { formatDate } from '../../../../../../utils/unitUtils';

interface MiscTabProps {
  materials: MaterialData[];
  selectedPipeSize: number;
  currentPage: number;
  materialsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  refreshMaterials: () => Promise<void>;
  onRowDelete?: (materialId: string | number, category: string) => Promise<void>;
  deletingRows?: Set<string>;
  documentTags: DocumentTag[];
  setDocumentTags: (any: any) => void;
  pipeSizesData: any;
  canEdit?: boolean;
  onOpenViewer?: (fileOrFiles: any | any[]) => void;
}

function formatMiscManufacturer(material: MaterialData): string {
  const extra = material.details?.miscExtraDetails as { miscManufacturer?: string } | undefined;
  return extra?.miscManufacturer || '-';
}

export const MiscTab: React.FC<MiscTabProps> = React.memo(
  ({
    materials,
    currentPage,
    materialsPerPage,
    onPageChange,
    onItemsPerPageChange,
    refreshMaterials,
    onRowDelete,
    deletingRows,
    documentTags,
    setDocumentTags,
    pipeSizesData,
    canEdit = true,
    onOpenViewer,
  }) => {
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<any | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
    const [itemToUpdate, setItemToUpdate] = useState<MaterialData | null>(null);
    const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState<MaterialData | null>(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<any>(null);
    const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

    const {
      searchQuery,
      setSearchQuery,
      filters,
      onFilterChange,
      uniqueProductionTypes,
      uniquePartNumbers,
      uniqueManufacturers,
      filteredMaterials,
      hasActiveFilters,
      filteredCount,
      onResetFilters,
    } = useMiscFilters({ materials });

    const miscMaterials = filteredMaterials.filter((m: MaterialData) => m.materialCategory === 'Misc');

    const handleConfirmDelete = async () => {
      try {
        setDeleteLoading(true);
        if (onRowDelete) {
          await onRowDelete(itemToDelete?.materialId, itemToDelete?.materialCategory);
          setIsDeleteModalVisible(false);
          setItemToDelete(null);
          setDeleteLoading(false);
        } else {
          const res = await deleteMaterials([itemToDelete?.materialId], itemToDelete?.materialCategory);
          if (res) {
            setIsDeleteModalVisible(false);
            setItemToDelete(null);
            setDeleteLoading(false);
            refreshMaterials();
          }
        }
      } catch {
        setDeleteLoading(false);
        setIsDeleteModalVisible(false);
      }
    };

    const handleUpdateMaterial = async () => {
      await refreshMaterials();
    };

    const handleUpload = () => {
      refreshMaterials();
    };

    const handleDocumentClick = (material: MaterialData) => {
      setSelectedMaterial(material);
      setIsDocumentModalVisible(true);
    };

    const handleDocumentDownload = (document: any) => {
      setIsDocumentModalVisible(false);
      if (onOpenViewer) {
        onOpenViewer(document);
      } else if (document.fileUrl) {
        window.open(document.fileUrl, '_blank');
      }
    };

    const handleDeleteDocument = (doc: any) => {
      setDocumentToDelete(doc);
      setIsDocumentDeleteModalOpen(true);
    };

    const handleCancelDeleteDocument = () => {
      setIsDocumentDeleteModalOpen(false);
      setDocumentToDelete(null);
    };

    const handleConfirmDeleteDocument = async () => {
      if (!documentToDelete) return;
      setDeleteDocumentLoading(true);
      try {
        const materialTraceabilityId = documentToDelete.id || documentToDelete.materialTraceabilityId;
        if (!materialTraceabilityId) {
          setDeleteDocumentLoading(false);
          return;
        }
        const payload = { materialDocsIds: [Number(materialTraceabilityId)] };
        const deleteResponse = await DeleteMaterialDocument(payload);
        if (deleteResponse) {
          refreshMaterials();
          handleCancelDeleteDocument();
          setIsDocumentModalVisible(false);
          setSelectedMaterial(null);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setDeleteDocumentLoading(false);
      }
    };

    const transformDocumentsForModal = (material: MaterialData) => {
      const documents = material.documents || [];
      return documents.flatMap((doc) => {
        if ((doc as any).materials && Array.isArray((doc as any).materials)) {
          return (doc as any).materials.map((mat: any) => ({
            id: mat.materialTraceabilityId || Math.random().toString(),
            fileName: mat.fileName,
            fileUrl: mat.fileUrl,
            uploadDate: mat.uploadedAt || mat.createdAt,
            type: 'document',
          }));
        }
        return [
          {
            id: (doc as any).id || Math.random().toString(),
            fileName: (doc as any).fileName,
            fileUrl: (doc as any).fileUrl,
            uploadDate: (doc as any).uploadedAt,
            type: 'document',
          },
        ];
      });
    };

    const renderItemDetails = (item: DeleteItem) => (
      <div className="text-sm text-gray-700 py-2 border-b border-gray-200 last:border-b-0">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div>• Part: {item.details?.miscPartNumber || '-'}</div>
          <div>• Heat: {item.details?.miscHeatNumber || '-'}</div>
          <div>• Lot: {item.details?.miscLotNumber || '-'}</div>
          <div>• Location: {item.details?.miscLocation || '-'}</div>
        </div>
      </div>
    );

    const hasAnyMaterials = miscMaterials.length > 0;
    if (hasActiveFilters && !hasAnyMaterials) {
      return (
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No miscellaneous materials match filters</h3>
            <p className="text-gray-500 max-w-sm">Clear filters or adjust your search.</p>
          </div>
        </div>
      );
    }

    return (
      <>
        <MiscFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={onFilterChange}
          uniqueProductionTypes={uniqueProductionTypes}
          uniquePartNumbers={uniquePartNumbers}
          uniqueManufacturers={uniqueManufacturers}
          hasActiveFilters={hasActiveFilters}
          filteredCount={filteredCount}
          onResetFilters={onResetFilters}
        />
        <Card>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                <Table className="min-w-[1400px]" containerClassName="">
                  <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>
                      <TableHead>Material type</TableHead>
                      <TableHead>Part Number</TableHead>
                      <TableHead>Heat Number</TableHead>
                      {/* <TableHead>Lot #</TableHead> */}
                      <TableHead>Job Number</TableHead>
                      <TableHead>PO Number</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Size / weight</TableHead>
                      <TableHead>Manufacturer</TableHead>
                      <TableHead>Production</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead className="text-center w-[100px]">Date</TableHead>
                      <TableHead className="text-center">Documents</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {miscMaterials.length > 0 ? (
                      miscMaterials
                        .slice((currentPage - 1) * materialsPerPage, currentPage * materialsPerPage)
                        .map((row) => (
                          <TableRow key={row.materialId}>
                            <TableCell className="max-w-[140px] truncate" title={String(row.details?.miscMaterialTypeLabels || '')}>
                              {(row.details?.miscMaterialTypeName as string) || '-'}
                            </TableCell>
                            <TableCell>{row.details?.miscPartNumber || '-'}</TableCell>
                            <TableCell>{row.details?.miscHeatNumber || '-'}</TableCell>
                            {/* <TableCell>{row.details?.miscLotNumber || '-'}</TableCell> */}
                            <TableCell>{row.details?.miscJobNumber || '-'}</TableCell>
                            <TableCell>{row.details?.miscPoNumber || '-'}</TableCell>
                            <TableCell>{row.details?.miscQuantity ?? '-'}</TableCell>
                            <TableCell className="max-w-[120px] truncate">{row.details?.miscSizeOrWeight || '-'}</TableCell>
                            <TableCell className="max-w-[160px] truncate">{formatMiscManufacturer(row)}</TableCell>
                            <TableCell>{row.details?.miscProductionType || '-'}</TableCell>
                            <TableCell className="max-w-[140px] truncate" title={String(row.details?.miscLocation || '')}>
                              {row.details?.miscLocation || '-'}
                            </TableCell>
                            <TableCell className="text-center w-[150px]">
                              {row.materialDate ? formatDate(row.materialDate) : '-'}
                            </TableCell>
                            <TableCell className="text-center w-[80px]">
                              <div
                                className={`flex items-center gap-2 ${!row.documents || row.documents.length === 0 ? 'justify-center' : ''}`}
                              >
                                <Button
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                  onClick={() => {
                                    setSelectedMaterial(row);
                                    setIsUploadModalOpen(true);
                                  }}
                                  title={`Upload ${row.materialCategory}`}
                                  variant="outline"
                                  disabled={!canEdit}
                                >
                                  <Upload className="h-4 w-4" />
                                </Button>
                                {row.documents && row.documents.length > 0 && (
                                  <Button
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                    title="View Documents"
                                    variant="outline"
                                    onClick={() => handleDocumentClick(row)}
                                  >
                                    <FileText className="h-4 w-4" />
                                    {row.documents.length}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center gap-2 justify-center">
                                <Button
                                  onClick={() => {
                                    setItemToUpdate(row);
                                    setIsUpdateModalVisible(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                  title="Edit Material"
                                  variant="outline"
                                  disabled={!canEdit || deletingRows?.has(String(row.materialId))}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                                  onClick={() => {
                                    setIsDeleteModalVisible(true);
                                    setItemToDelete(row);
                                  }}
                                  title="Delete Material"
                                  variant="outline"
                                  disabled={!canEdit || deletingRows?.has(String(row.materialId))}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={14} className="text-center text-gray-500">
                          No miscellaneous materials found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <Pagination
              currentPage={currentPage}
              totalItems={miscMaterials.length}
              itemsPerPage={materialsPerPage}
              onPageChange={onPageChange}
              onItemsPerPageChange={onItemsPerPageChange}
              pageSizeOptions={[10, 25, 50, 100]}
              showPageNumbers={true}
              maxVisiblePages={5}
            />
          </CardContent>
        </Card>

        <DeleteConfirmationDialog
          isOpen={isDeleteModalVisible}
          onClose={() => setIsDeleteModalVisible(false)}
          onConfirm={handleConfirmDelete}
          itemType="Material"
          itemName={itemToDelete?.details?.miscPartNumber}
          itemToDelete={itemToDelete}
          detailsRenderer={renderItemDetails}
          isLoading={deleteLoading}
        />
        <UpdateMaterialDialog
          isOpen={isUpdateModalVisible}
          onClose={() => setIsUpdateModalVisible(false)}
          onUpdate={handleUpdateMaterial}
          material={itemToUpdate}
          project={{} as any}
          unit="inches"
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          selectedPipe={pipeSizesData}
        />

        {selectedMaterial && (
          <DocumentDownloadModal
            isOpen={isDocumentModalVisible}
            onClose={() => {
              setIsDocumentModalVisible(false);
              setSelectedMaterial(null);
            }}
            documents={transformDocumentsForModal(selectedMaterial)}
            onDownload={handleDocumentDownload}
            onDelete={handleDeleteDocument}
            title="Miscellaneous material documents"
            canDelete={canEdit}
          />
        )}

        <DeleteConfirmationDialog
          isOpen={isDocumentDeleteModalOpen}
          onClose={handleCancelDeleteDocument}
          onConfirm={handleConfirmDeleteDocument}
          isLoading={deleteDocumentLoading}
          itemToDelete={documentToDelete}
          itemType="Material Document"
          itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
          title="Delete Material Document"
        />

        <MaterialDocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUpload={handleUpload}
          title="Upload Material Document"
          acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
          multiple={true}
          maxFiles={1}
          buttonText="Select Document"
          label="Document"
          description="Upload material documents (PDF, Excel, DOC, etc...)"
          material={selectedMaterial || undefined}
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          materialType="Misc"
          selectedPipeSize={pipeSizesData}
        />
      </>
    );
  }
);
